import { BurstObservation, ModelParams, MotorParams, NF, PlayerParams, SigVocab } from './cost';

// Fitting the hazard model.
//
// The objective is exactly the simulator's generative model, which is the point: whatever the
// optimizer maximizes is what the simulator will later sample from, so a good fit and a
// realistic simulation are the same thing rather than two things that have to be kept in step.
//
//   J = Σ_b [ log Σ_{i∈cand(b)} λ_i  −  g_b · Σ_{i∈risk(b)} λ_i ]  −  ½ρ‖β‖²  −  ½ρ‖α‖²  −  Σ δ²/2σ²
//
// The first term says "one of the deductions that explains this move fired"; the second is the
// survival term — everything else that was available did *not* fire during those g_b
// milliseconds, which is what stops the model from simply making every rate enormous.
//
// Attribution posteriors fall out for free as λ_i / Σ_{j∈cand} λ_j, so there is no separate EM
// step: the ambiguity in "which technique did they use" is resolved by the same parameters that
// are being fitted.
//
// Because the rate is log-linear, ∂λ/∂w = λ·x_w and every gradient below is a weighted sum of
// feature rows. Adam, full batch, no dependencies.

/** Milliseconds are a terrible unit for a rate — rates land near e^-8. Fit in seconds instead. */
const MS_PER_UNIT = 1000;

export interface FitOptions {
  iterations?: number;
  learningRate?: number;
  /** L2 on β and α. */
  ridge?: number;
  /** Prior sd on the per-player, per-signature offsets. */
  deltaSd?: number;
  /** Players with fewer observations than this get θ only, δ shrunk fully to 0. */
  minObsForDelta?: number;
  onProgress?: (iter: number, logLik: number) => void;
}

export interface PlayerObservations {
  key: string;
  observations: BurstObservation[];
}

export interface FitResult {
  params: ModelParams;
  vocab: SigVocab;
  /** Mean log-likelihood per observation at the end — comparable across fits, unlike the sum. */
  logLikPerObs: number;
  iterations: number;
  observations: number;
  /** Players whose δ was fitted rather than pinned at 0. */
  fittedDelta: string[];
}

/**
 * Least-squares `burstDuration ≈ intercept + perCell · nCells` over pure bookkeeping bursts.
 *
 * Fitted first, separately, and frozen. Bookkeeping bursts are unambiguous, numerous and purely
 * motor, so this removes the single largest nuisance from the search fit — on a 9×9 in manual
 * mode motor time alone is 25–30s, and left in it would make "difficulty" a proxy for board size.
 */
export function fitMotor(acc: { n: number; sx: number; sy: number; sxx: number; sxy: number }): MotorParams {
  const fallback = { interceptMs: 250, perCellMs: 120 };
  if (acc.n < 8) return fallback;
  const denom = acc.n * acc.sxx - acc.sx * acc.sx;
  if (Math.abs(denom) < 1e-9) return fallback;
  const perCellMs = (acc.n * acc.sxy - acc.sx * acc.sy) / denom;
  const interceptMs = (acc.sy - perCellMs * acc.sx) / acc.n;
  // Guard against a degenerate sample producing a negative or absurd slope; a nonsensical motor
  // model would be subtracted out of every gap and poison the search fit silently.
  if (!(perCellMs > 0) || perCellMs > 2000 || !(interceptMs > -2000)) return fallback;
  return { interceptMs: Math.max(0, interceptMs), perCellMs };
}

/** Adam, in the fifteen lines it actually takes. */
class Adam {
  private m: Float64Array;
  private v: Float64Array;
  private t = 0;
  constructor(private n: number, private lr: number) {
    this.m = new Float64Array(n);
    this.v = new Float64Array(n);
  }
  step(w: Float64Array, g: Float64Array): void {
    this.t++;
    const b1 = 0.9, b2 = 0.999, eps = 1e-8;
    const c1 = 1 - Math.pow(b1, this.t), c2 = 1 - Math.pow(b2, this.t);
    for (let i = 0; i < this.n; i++) {
      this.m[i] = b1 * this.m[i] + (1 - b1) * g[i];
      this.v[i] = b2 * this.v[i] + (1 - b2) * g[i] * g[i];
      w[i] += this.lr * (this.m[i] / c1) / (Math.sqrt(this.v[i] / c2) + eps);
    }
  }
}

/**
 * Fit α (global technique difficulty), β (feature weights), and per-player θ and δ.
 *
 * `motor` must already be fitted and is used to charge each observation for the clicking the
 * player had to do, so the search rate is not silently absorbing motor time.
 */
export function fitModel(
  players: PlayerObservations[], motor: Record<string, MotorParams>, opts: FitOptions = {},
): FitResult {
  // 2000, not a few hundred: recovery tests show β settles quickly but the α *differences*
  // (which is what "this technique is harder than that one" means) need ~3000 full-batch steps.
  const iterations = opts.iterations ?? 2000;
  const lr = opts.learningRate ?? 0.05;
  const ridge = opts.ridge ?? 0.01;
  const deltaSd = opts.deltaSd ?? 0.5;
  const minObsForDelta = opts.minObsForDelta ?? 400;

  const vocab = SigVocab.from(players.flatMap(p => p.observations.flatMap(o => o.sigKeys)));
  const S = vocab.size;

  // Pre-resolve every signature string to its dense id once. String hashing in the inner loop
  // would dominate everything else here.
  interface Obs { sig: Int32Array; x: Float32Array; nRisk: number; cand: Int32Array; dt: number }
  const byPlayer = new Map<string, Obs[]>();
  let total = 0;
  for (const p of players) {
    const list: Obs[] = [];
    for (const o of p.observations) {
      // The gap includes the clicking for the burst that follows it (a drag is one event with
      // one timestamp, so its motor time lands in the gap *before* it). Charge that out; what
      // is left is search time, which is what the hazard describes.
      const m = motor[p.key] ?? { interceptMs: 250, perCellMs: 120 };
      const searchMs = o.gapMs - m.interceptMs - m.perCellMs * o.candidates.length;
      const dt = Math.max(50, searchMs) / MS_PER_UNIT;
      list.push({
        sig: Int32Array.from(o.sigKeys, k => vocab.idOf(k)),
        x: o.x, nRisk: o.nRisk, cand: Int32Array.from(o.candidates), dt,
      });
      total++;
    }
    byPlayer.set(p.key, list);
  }

  const keys = [...byPlayer.keys()];
  const fittedDelta = keys.filter(k => (byPlayer.get(k)!.length) >= minObsForDelta);
  const fitsDelta = new Set(fittedDelta);

  // Layout: [ alpha(S) | beta(NF) | theta(P) | delta(P·S, only for the fitted players) ]
  const P = keys.length;
  const deltaBase = S + NF + P;
  const deltaSlot = new Map<string, number>();
  let cursor = deltaBase;
  for (const k of keys) {
    if (fitsDelta.has(k)) { deltaSlot.set(k, cursor); cursor += S; }
  }
  const N = cursor;
  const w = new Float64Array(N);
  const g = new Float64Array(N);
  // Start with a rate of about one deduction per second, which is the corpus median pause.
  for (let i = 0; i < S; i++) w[i] = 0;
  const adam = new Adam(N, lr);

  let logLik = 0;
  for (let iter = 0; iter < iterations; iter++) {
    g.fill(0);
    logLik = 0;

    for (let pi = 0; pi < P; pi++) {
      const key = keys[pi];
      const thetaIdx = S + NF + pi;
      const dSlot = deltaSlot.get(key);

      for (const o of byPlayer.get(key)!) {
        // λ for every deduction that was available.
        const lam = new Float64Array(o.nRisk);
        let sumAll = 0;
        for (let i = 0; i < o.nRisk; i++) {
          const s = o.sig[i];
          let z = w[thetaIdx];
          if (s >= 0) {
            z += w[s];
            if (dSlot !== undefined) z += w[dSlot + s];
          }
          const at = i * NF;
          for (let k = 0; k < NF; k++) z += w[S + k] * o.x[at + k];
          // Clamp before exp: an early Adam step can otherwise overflow to Infinity and the
          // whole fit becomes NaN with no way to tell where it happened.
          const l = Math.exp(Math.max(-30, Math.min(20, z)));
          lam[i] = l;
          sumAll += l;
        }
        let sumCand = 0;
        for (const i of o.cand) sumCand += lam[i];
        if (!(sumCand > 0) || !isFinite(sumAll)) continue;

        logLik += Math.log(sumCand) - o.dt * sumAll;

        // ∂/∂w [ log Σ_cand λ − dt·Σ_all λ ] = Σ_cand (λ_i/Σ_cand) x_i − dt·Σ_all λ_j x_j.
        // The first sum is the attribution posterior, which is why no separate E-step exists.
        const addTo = (i: number, coef: number) => {
          const s = o.sig[i];
          if (s >= 0) {
            g[s] += coef;
            if (dSlot !== undefined) g[dSlot + s] += coef;
          }
          g[thetaIdx] += coef;
          const at = i * NF;
          for (let k = 0; k < NF; k++) g[S + k] += coef * o.x[at + k];
        };
        for (const i of o.cand) addTo(i, lam[i] / sumCand);
        for (let i = 0; i < o.nRisk; i++) addTo(i, -o.dt * lam[i]);
      }
    }

    // Penalties (gradient of −½ρ‖·‖² and of the δ prior).
    for (let i = 0; i < S; i++) { g[i] -= ridge * w[i]; logLik -= 0.5 * ridge * w[i] * w[i]; }
    for (let k = 0; k < NF; k++) {
      const i = S + k;
      g[i] -= ridge * w[i];
      logLik -= 0.5 * ridge * w[i] * w[i];
    }
    const dPrec = 1 / (deltaSd * deltaSd);
    for (const slot of deltaSlot.values()) {
      for (let s = 0; s < S; s++) {
        const i = slot + s;
        g[i] -= dPrec * w[i];
        logLik -= 0.5 * dPrec * w[i] * w[i];
      }
    }

    adam.step(w, g);
    opts.onProgress?.(iter, logLik / Math.max(1, total));
  }

  const playersOut: Record<string, PlayerParams> = {};
  for (let pi = 0; pi < P; pi++) {
    const key = keys[pi];
    const slot = deltaSlot.get(key);
    const delta = new Float64Array(S);
    if (slot !== undefined) for (let s = 0; s < S; s++) delta[s] = w[slot + s];
    playersOut[key] = {
      theta: w[S + NF + pi],
      delta,
      motor: motor[key] ?? { interceptMs: 250, perCellMs: 120 },
    };
  }

  return {
    params: {
      sigKeys: vocab.keys,
      alpha: w.slice(0, S),
      beta: w.slice(S, S + NF),
      players: playersOut,
    },
    vocab,
    logLikPerObs: logLik / Math.max(1, total),
    iterations,
    observations: total,
    fittedDelta,
  };
}

/**
 * The frozen reference player every published difficulty number is quoted against: the mean of
 * the fitted population, with every δ = 0.
 *
 * θ is the *mean of the fitted θs*, not 0. The plan specified 0, but θ and α are only identified
 * up to a shared constant and the ridge pins α, not θ — so θ = 0 would silently mean "faster
 * than every real player" and make `refTimeMs` an estimate nobody could hit. Only players with
 * enough observations to have a meaningful θ are averaged.
 *
 * Never let a refit move every published number: bump the stored version instead.
 */
export function referencePlayer(fit: FitResult, weights: Record<string, number> = {}): PlayerParams {
  const entries = Object.entries(fit.params.players);
  let wSum = 0, thetaSum = 0, interceptSum = 0, perCellSum = 0;
  for (const [key, p] of entries) {
    const w = weights[key] ?? 1;
    wSum += w;
    thetaSum += w * p.theta;
    interceptSum += w * p.motor.interceptMs;
    perCellSum += w * p.motor.perCellMs;
  }
  const n = Math.max(1e-9, wSum);
  return {
    theta: thetaSum / n,
    delta: new Float64Array(fit.params.alpha.length),
    motor: { interceptMs: interceptSum / n, perCellMs: perCellSum / n },
  };
}

/** Serializable form — Float64Arrays do not survive JSON. */
export function serializeParams(p: ModelParams): unknown {
  return {
    sigKeys: p.sigKeys,
    alpha: [...p.alpha],
    beta: [...p.beta],
    players: Object.fromEntries(Object.entries(p.players).map(([k, v]) => [k, {
      theta: v.theta, delta: [...v.delta], motor: v.motor,
    }])),
  };
}

export function deserializeParams(raw: unknown): ModelParams {
  const o = raw as {
    sigKeys: string[]; alpha: number[]; beta: number[];
    players: Record<string, { theta: number; delta: number[]; motor: MotorParams }>;
  };
  return {
    sigKeys: o.sigKeys,
    alpha: Float64Array.from(o.alpha),
    beta: Float64Array.from(o.beta),
    players: Object.fromEntries(Object.entries(o.players).map(([k, v]) => [k, {
      theta: v.theta, delta: Float64Array.from(v.delta), motor: v.motor,
    }])),
  };
}
